import React, { useState, useEffect } from "react";
import { array, func, object } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { hasActiveMembership } from "../../../utils/membership-utils";
import GuestBuyReportModal from "../GuestBuyReportModal";

import styles from "./user-type-selection.m.css";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";

const UserTypeSelection = ({
  plans = [],
  story,
  initRazorPayPayment,
  getSubscriptionForUser,
  member,
  onSignInClick,
}) => {
  const [selected, setSelected] = useState(null);
  const [showGuestBuyModal, setShowGuestBuyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadataWaitComplete, setMetadataWaitComplete] = useState(false);

  // Get subscriberMetadata from Redux
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));

  // Check if user is logged in
  const isLoggedIn = !!member;

  // Wait briefly for metadata when user is logged in but metadata hasn't arrived
  useEffect(() => {
    if (isLoggedIn && !subscriberMetadata && !metadataWaitComplete) {
      // Give metadata time to load (max 2 seconds)
      const timeout = setTimeout(() => {
        setMetadataWaitComplete(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isLoggedIn, subscriberMetadata, metadataWaitComplete]);

  // Reset wait state when metadata arrives
  useEffect(() => {
    if (subscriberMetadata) {
      setMetadataWaitComplete(true);
    }
  }, [subscriberMetadata]);

  // Check if we're still waiting for metadata
  const isWaitingForMetadata = isLoggedIn && !subscriberMetadata && !metadataWaitComplete;

  // Check if user has active membership using common utility
  const isActiveMember = hasActiveMembership(subscriberMetadata);

  // Check if a plan is for paid members (has custom_attributes with paidMember = "true")
  const isPaidMemberPlan = (plan) => {
    const customAttributes = plan.custom_attributes || [];
    const paidMemberAttr = customAttributes.find(
      (attr) => attr.name === "paidMember"
    );
    return paidMemberAttr?.value === "true";
  };

  // Determine if a plan should be disabled
  const isPlanDisabled = (plan) => {
    const isForumPlan = isPaidMemberPlan(plan);
    
    // If user is an active forum member, only forum plans are enabled
    if (isActiveMember) {
      return !isForumPlan;
    }
    
    // For guests or non-forum members, only non-forum plans are enabled
    return isForumPlan;
  };

  // Filter plans based on membership status
  // - Show all plans for all users
  // - Selection logic is handled by isPlanDisabled (disabling based on membership status)
  const getFilteredPlans = () => {
    if (!plans || plans.length === 0) return [];
    return plans;
  };

  const filteredPlans = getFilteredPlans();

  // Auto-select the first enabled plan
  useEffect(() => {
    if (!filteredPlans || filteredPlans.length === 0) return;

    // Find the first plan that is not disabled
    const firstEnabledPlan = filteredPlans.find((plan) => !isPlanDisabled(plan));

    if (firstEnabledPlan && selected !== firstEnabledPlan.id) {
      setSelected(firstEnabledPlan.id);
    }
  }, [isLoggedIn, subscriberMetadata, filteredPlans]);

  // Show loading indicator while waiting for metadata
  if (isWaitingForMetadata) {
    return (
      <div className={styles["user-type-selection-container"]}>
        <LoadingIndicatorComponent />
      </div>
    );
  }

  // Return null if no plans after filtering
  if (!filteredPlans || filteredPlans.length === 0) {
    return null;
  }

  const handleSelect = (planId) => {
    // Only allow selection if user is logged in
    if (!isLoggedIn) {
      return;
    }
    // Check if the plan exists in filtered plans
    const plan = filteredPlans.find((p) => p.id === planId);
    if (!plan) {
      return;
    }
    // Check if plan is disabled (e.g., member plan for expired membership)
    if (isPlanDisabled(plan)) {
      return;
    }
    setSelected(planId);
    setError(null);
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    if (onSignInClick) {
      onSignInClick();
    }
  };

  const handleGetReport = async () => {
    if (!selected) {
      setError("Please select a plan to proceed.");
      return;
    }

    const selectedPlan = filteredPlans.find((p) => p.id === selected);
    if (!selectedPlan) {
      setError("Selected plan not found.");
      return;
    }


    setIsLoading(true);
    setError(null);

    try {
      // Use the plan object directly - it already has the correct id as number
      // According to AccessType API, we need subscription_plan_id, price_cents, price_currency

      // Call initRazorPayPayment with dynamic_asset type (like madrid)
      // Signature: initRazorPayPayment(plan, type, assetId, assetTitle, assetSlug)
      const paymentResponse = await initRazorPayPayment(
        selectedPlan,
        "standard"
      );


      if (paymentResponse.subscription) {
        // Reload the page to show full content
        window.location.reload();
      }
    } catch (err) {
      if (err?.error?.code === "subscription_preview_active_subscription") {
        setError("You already have an active subscription for this asset.");
      } else {
        setError(err.message || "Payment failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Button is disabled only when logged in and no plan selected or loading
  const isGetReportDisabled = !selected || isLoading;

  return (
    <div className={styles["user-type-selection-container"]}>
      <div className={`${styles["options-container"]}`}>
        {filteredPlans.map((plan) => {
          const isSelected = selected === plan.id;
          const planDisabled = isPlanDisabled(plan);
          const isVisuallyDisabled = planDisabled;

          return (
            <button
              key={plan.id}
              type="button"
              className={`${styles.option} ${isVisuallyDisabled ? styles["option-disabled"] : ""}`}
              onClick={() => handleSelect(plan.id)}
              disabled={isLoading || isVisuallyDisabled}
            >
              <div className={styles["radio-button-wrapper"]}>
                <div className={`${styles["radio-button"]} ${isSelected ? styles["radio-selected"] : ""} ${isVisuallyDisabled ? styles["radio-disabled"] : ""}`}>
                  {isSelected && <div className={styles["radio-inner"]} />}
                </div>
              </div>
              <div className={styles["option-content"]}>
                <span className={`${styles["option-label"]} ${isVisuallyDisabled ? styles["label-disabled"] : ""}`}>{plan.label || plan.title}</span>
                <span className={`${styles["option-price"]} ${isVisuallyDisabled ? styles["price-disabled"] : ""}`}>{plan.price}</span>
              </div>
            </button>
          );
        })}
      </div>
      {error && <div className={styles["error-message"]}>{error}</div>}
      {isLoggedIn ? (
        <button
          type="button"
          className={`${styles["get-report-btn"]} ${isGetReportDisabled ? styles.disabled : ""}`}
          disabled={isGetReportDisabled}
          onClick={handleGetReport}
        >
          {isLoading ? "Processing..." : "Get report"}
        </button>
      ) : (
        selected ? (
          <button
            type="button"
            className={`${styles["get-report-btn"]}`}
            disabled={isLoading}
            onClick={() => setShowGuestBuyModal(true)}
          >
            {isLoading ? "Processing..." : "Buy Report"}
          </button>
        ) : (
          <button
            type="button"
            className={styles["sign-in-btn"]}
            onClick={handleSignIn}
          >
            Sign in
          </button>
        )
      )}

      {showGuestBuyModal && (
        <GuestBuyReportModal
          isOpen={showGuestBuyModal}
          onClose={() => setShowGuestBuyModal(false)}
          selectedPlanId={selected}
          initRazorPayPayment={initRazorPayPayment}
          story={story}
          onSignInClick={onSignInClick}
        />
      )}
    </div>
  );
};

UserTypeSelection.propTypes = {
  plans: array,
  story: object,
  initRazorPayPayment: func,
  getSubscriptionForUser: func,
  member: object,
  onSignInClick: func,
};

export default UserTypeSelection;
